/**
 * Inspection executors — get_sheet_names, get_active_sheet_info.
 *
 * These are read-only tools that gather metadata about the workbook.
 * They require intermediate syncs to materialise proxy object properties.
 *
 * See docs/excel-client-side-tools/phase-3-client-tool-executors.md
 */

/* global Excel */

import { getSheet, resolveRange } from "./utils";

// ---------------------------------------------------------------------------
// get_sheet_names
// ---------------------------------------------------------------------------

/**
 * Return the names of all worksheets in the workbook, plus which is active.
 *
 * No args required.
 */
export async function getSheetNamesCore(
  ctx: Excel.RequestContext
): Promise<unknown> {
  const sheets = ctx.workbook.worksheets;
  sheets.load("items/name,items/position,items/visibility");

  const activeSheet = ctx.workbook.worksheets.getActiveWorksheet();
  activeSheet.load("name");

  await ctx.sync();

  return {
    sheets: sheets.items.map((s) => ({
      name: s.name,
      position: s.position,
      visibility: s.visibility,
    })),
    activeSheet: activeSheet.name,
    count: sheets.items.length,
  };
}

// ---------------------------------------------------------------------------
// get_active_sheet_info
// ---------------------------------------------------------------------------

/**
 * Return detailed info about the active sheet: name, used range dimensions,
 * and a preview of the first few rows.
 *
 * Expected args:
 *   previewRows?: number — How many rows to preview (default 5, max 20)
 *   sheet?:       string — Optional sheet name (defaults to active)
 */
export async function getActiveSheetInfoCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const previewRows = Math.min(Math.max(Number(args?.previewRows) || 5, 1), 20);
  const sheetName = args?.sheet as string | undefined;

  // Workbook-level selection — reported independently of the `sheet` arg so
  // the LLM can see what the user actually has highlighted. Done in an
  // isolated sync because `getSelectedRange()` throws at sync time when the
  // active selection is not a range (e.g. a chart or shape is selected),
  // and we don't want that to fail the whole orientation call.
  let selection: {
    address: string;
    rowCount: number;
    columnCount: number;
  } | null = null;
  try {
    const selRange = ctx.workbook.getSelectedRange();
    selRange.load("address,rowCount,columnCount");
    await ctx.sync();
    selection = {
      address: selRange.address,
      rowCount: selRange.rowCount,
      columnCount: selRange.columnCount,
    };
  } catch {
    selection = null;
  }

  const sheet = getSheet(ctx, sheetName);
  sheet.load("name,position");

  // UsedRange can throw if sheet is completely empty
  const usedRange = sheet.getUsedRangeOrNullObject();
  usedRange.load("address,rowCount,columnCount");

  await ctx.sync();

  if (usedRange.isNullObject) {
    return {
      name: sheet.name,
      position: sheet.position,
      isEmpty: true,
      usedRange: null,
      selection,
      preview: [],
    };
  }

  // Load a preview of the first N rows
  const previewRange = usedRange
    .getRow(0)
    .getResizedRange(
      Math.min(previewRows - 1, usedRange.rowCount - 1),
      usedRange.columnCount - 1
    );
  previewRange.load("values,address");

  await ctx.sync();

  return {
    name: sheet.name,
    position: sheet.position,
    isEmpty: false,
    usedRange: {
      address: usedRange.address,
      rowCount: usedRange.rowCount,
      columnCount: usedRange.columnCount,
    },
    selection,
    preview: previewRange.values,
  };
}

// ---------------------------------------------------------------------------
// read_formatting
// ---------------------------------------------------------------------------

/**
 * Above this cell count we return a single range-level format summary instead
 * of per-cell properties, to keep the sync payload small. Per-cell detail needs
 * `getCellProperties` (a full property matrix), which is expensive for large
 * ranges.
 */
const MAX_FMT_CELLS = 2000;

/**
 * Read existing cell formatting from a range: per-cell fill color, font,
 * number format, and alignment, plus row heights and column widths.
 *
 * `read_range` returns values/formulas; this is the formatting counterpart. Fill /
 * font come from `getCellProperties`; row heights / column widths come from
 * `getRowProperties` / `getColumnProperties` (they're not part of cell
 * properties); number formats from `range.numberFormat`.
 *
 * Self-syncing — manages its own `ctx.sync()` calls.
 *
 * Expected args:
 *   range:      string
 *   sheetName?: string
 */
export async function readFormattingCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;

  if (!rangeAddr) {
    throw new Error(
      "read_formatting requires a `range` argument (e.g. 'A1:D10')."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);
  range.load("address,rowCount,columnCount");
  await ctx.sync();

  const cellCount = range.rowCount * range.columnCount;

  // Large range — one range-level summary. Aggregate format properties read
  // back blank ("" / null) when the property is not uniform across the range.
  if (cellCount > MAX_FMT_CELLS) {
    const fmt = range.format;
    fmt.load(
      "rowHeight,columnWidth,horizontalAlignment,verticalAlignment,wrapText"
    );
    fmt.fill.load("color");
    fmt.font.load("name,size,color,bold,italic,underline");
    await ctx.sync();

    return {
      address: range.address,
      rowCount: range.rowCount,
      columnCount: range.columnCount,
      perCell: false,
      note:
        `Range has ${cellCount} cells (> ${MAX_FMT_CELLS}); returning a ` +
        "range-level summary. A blank value means that property is not uniform " +
        "across the range — request a smaller range for per-cell formatting.",
      summary: {
        fillColor: fmt.fill.color,
        font: {
          name: fmt.font.name,
          size: fmt.font.size,
          color: fmt.font.color,
          bold: fmt.font.bold,
          italic: fmt.font.italic,
          underline: fmt.font.underline,
        },
        horizontalAlignment: fmt.horizontalAlignment,
        verticalAlignment: fmt.verticalAlignment,
        wrapText: fmt.wrapText,
        rowHeight: fmt.rowHeight,
        columnWidth: fmt.columnWidth,
      },
    };
  }

  // Per-cell formatting.
  const cellProps = range.getCellProperties({
    format: {
      fill: { color: true },
      font: {
        name: true,
        size: true,
        color: true,
        bold: true,
        italic: true,
        underline: true,
      },
      horizontalAlignment: true,
      verticalAlignment: true,
      wrapText: true,
    },
  });
  const rowProps = range.getRowProperties({ format: { rowHeight: true } });
  const colProps = range.getColumnProperties({ format: { columnWidth: true } });
  range.load("numberFormat");
  await ctx.sync();

  const numberFormats = range.numberFormat as unknown[][];
  const cells = cellProps.value.map((row, r) =>
    row.map((cell, c) => {
      const f = cell.format;
      return {
        fillColor: f?.fill?.color,
        font: {
          name: f?.font?.name,
          size: f?.font?.size,
          color: f?.font?.color,
          bold: f?.font?.bold,
          italic: f?.font?.italic,
          underline: f?.font?.underline,
        },
        horizontalAlignment: f?.horizontalAlignment,
        verticalAlignment: f?.verticalAlignment,
        wrapText: f?.wrapText,
        numberFormat: numberFormats?.[r]?.[c],
      };
    })
  );

  return {
    address: range.address,
    rowCount: range.rowCount,
    columnCount: range.columnCount,
    perCell: true,
    cells,
    rowHeights: rowProps.value.map((p) => p.format?.rowHeight),
    columnWidths: colProps.value.map((p) => p.format?.columnWidth),
  };
}
